import { html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { StoreState } from '../../../store/store.interface';
import { selectRequest, selectTotal, selectSearchPageType, selectSelectedCatalog } from '../../search/store/search.selector';
import emit from '../../../common/events';
import { PaginationBase } from './../pagination-base';
import "../message/pagination-message";
import { SearchResults } from '../../search/search-results';
import store from '../../../store/store';
import { connect } from 'pwa-helpers';
import { performSearch } from '../../search/thunks/search.thunk';
import { setCurrentPageIndex, setCurrentPageLength } from '../../search/store/search.store';

export declare interface PaginationPage {
  label: string;
  index: number;
}

/**
 * An element to paginate search results
 *
 * @csspart page-button - Each page button of the pagination.
 * @csspart current-page-button - The current page button.
 * @csspart previous - The previous button arrow.
 * @csspart next - The next button arrow.
 *
 * @event go-to-page - A user has clicked a page within the pagination.
 *        <br><br><span style="color: green">&check;</span> dataLayer
 *        <br><span style="color: green">&check;</span> bubbles
 *        <br><br>event.detail: { searchTerms, catalog, pageLength, start, facets }
 */
@customElement('mhecomm-pagination')
export class Pagination extends connect(store)(PaginationBase) {
  /**
   * Overriding base components unloadedHeight
   */
  unloadedHeight = '58px';

  /**
   * Which store to show a cart for. If not set it will show the national cart.
   */
  @property()
  store?: string;

  @property()
  categoryId: string | undefined;

  /**
   * The number of pages to show in the pagination. This does not include the first and last pages or the arrows. It should be an odd number.
   */
  @property({ type: Number })
  maxPagesShown = 3;

  /**
   * The message to display to the user when there are results. The following placeholders are available: %first% %last% %total% %term%
   */
  @property()
  message = 'Showing %first%-%last% of %total% Results';

  renderComp() {
    return this.paginationComponent();
  }

  override showPaginationMsg() {
    return html`
      <mhecomm-pagination-message
        message="${this.message}"
        id="pagination_message"
        data-automation-id="pagination_message"
      >
      </mhecomm-pagination-message>
    `;
  }

  override goToPageHook(pageIndex: number) {
    store.dispatch(
      performSearch({
        start: pageIndex * this.pageLength + 1,
        categoryId: this.categoryId,
      })
    );
    // What id should we hook up? Pass as property?
    (document.querySelector('mhecomm-search-results') as SearchResults)?.scrollToResult();
    this.currentPage = pageIndex;
    const activePageId = `page-${pageIndex + 1}`;
    setTimeout(() => {
      this.shadowRoot?.getElementById(activePageId)?.focus();
    }, 100);
}

  override emitAnalytics(pageIndex: number) {
    const state = store.getState() as StoreState; // Get the current state
    const catalog = selectSelectedCatalog(state);
    emit({
      element: this,
      name: 'go-to-page',
      detail: {
        paginationNumber: pageIndex + 1,
        store: this.store,
        catalog: catalog,
        searchPageType: selectSearchPageType(store.getState() as StoreState),
      },
      dataLayer: true,
    });
  }

  stateChanged(state: StoreState) {
    const request = selectRequest(state);
    const total = selectTotal(state) || 0;
    this.store = request?.store;
    let currentPage = 0;

    if (request && request.start >= request.pageLength) {
      currentPage = Math.floor((request.start - 1) / request.pageLength);
    }

    if (request && (this.total != total || this.currentPage != currentPage)) {
      this.total = total;
      this.currentPage = currentPage;
      this.pageLength = request.pageLength || 10;
      this.updatePages();
      store.dispatch(setCurrentPageIndex(currentPage + 1));
      store.dispatch(setCurrentPageLength(this.pageLength));
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-pagination': Pagination;
  }
}
